import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GrowthSeoProfileSelectorComponent } from '../../shared/design-system/growth-seo-profile-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  GrowthMarketingKitRun,
  GrowthMarketingService,
  GrowthOrganicFirstPolicy,
  GrowthPlaybookProgress,
  GrowthTgeConciergeSnapshot,
  GrowthTgeQuotaStatus,
  PolicyChannelRow
} from './growth-marketing.service';
import { buildGrowthMarketingChannelOptions } from './growth-channel.constants';

interface PlaybookStepView {
  stepId: string;
  action: string;
  contentType: string | null;
  channel: string;
  day: number;
  completed: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-growth-tge-concierge',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TagModule,
    TextareaModule,
    GrowthSeoProfileSelectorComponent,
    OfferingSelectorComponent,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.concierge.title')"
      [description]="t('growth.concierge.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.concierge.hint.title'"
        [descriptionKey]="'growth.concierge.hint.description'"
        [bulletKeys]="[
          'growth.concierge.hint.bullets.kit',
          'growth.concierge.hint.bullets.quota',
          'growth.concierge.hint.bullets.organicFirst',
          'growth.concierge.hint.bullets.playbook'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error()!" />
      } @else if (snapshot()) {
        <section class="card dashboard-grid">
          <article class="metric-card">
            <h3>{{ t('growth.concierge.cards.kitStatus') }}</h3>
            <p class="metric-value">{{ snapshot()!.kitStatus }}</p>
            @if (snapshot()!.latestKitRunId) {
              <a [routerLink]="['/growth/marketing/ace-studio']">{{ t('growth.concierge.actions.openContentStudio') }}</a>
            }
          </article>
          <article class="metric-card">
            <h3>{{ t('growth.concierge.cards.quota') }}</h3>
            <p>{{ t('growth.concierge.quota.kitRuns') }}: {{ quotaLabel(snapshot()!.quota.kitRunsUsed, snapshot()!.quota.kitRunsRemaining) }}</p>
            <p>{{ t('growth.concierge.quota.produceRuns') }}: {{ quotaLabel(snapshot()!.quota.produceRunsUsed, snapshot()!.quota.produceRunsRemaining) }}</p>
            @if (snapshot()!.quota.entitlement.source === 'Sandbox' || snapshot()!.quota.entitlement.source === 'SandboxFallback') {
              <p-tag severity="warn" [value]="t('growth.concierge.quota.sandboxBadge')" />
            }
          </article>
          <article class="metric-card">
            <h3>{{ t('growth.concierge.cards.organicFirst') }}</h3>
            <p>{{ snapshot()!.policy.enabled ? t('common.enabled') : t('common.disabled') }}</p>
            <p>{{ t('growth.concierge.organicFirst.paidAllowed') }}: {{ snapshot()!.policy.paidChannelsAllowed ? t('common.yes') : t('common.no') }}</p>
          </article>
          <article class="metric-card">
            <h3>{{ t('growth.concierge.cards.leads') }}</h3>
            <p class="metric-value">{{ snapshot()!.unreadLeadCount }}</p>
            <a routerLink="/growth/crm/leads">{{ t('growth.concierge.actions.reviewLeads') }}</a>
          </article>
        </section>

        <section class="card">
          <div class="section-header">
            <h2>{{ t('growth.concierge.actions.title') }}</h2>
            <ain-info-hint
              [titleKey]="'growth.concierge.actions.hint.title'"
              [descriptionKey]="'growth.concierge.actions.hint.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.concierge.cta.triggerKit')" (click)="triggerKit()" [disabled]="actionBusy()"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.concierge.cta.reviewKit')" (click)="loadLatestKit()" [disabled]="!snapshot()!.latestKitRunId"></button>
            <button pButton type="button" severity="help" [label]="t('growth.concierge.cta.approveBundle')" (click)="approveBundle()" [disabled]="!canApproveBundle() || actionBusy()"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.concierge.cta.completePlaybookStep')" (click)="completeNextPlaybookStep()" [disabled]="!nextPlaybookStep() || actionBusy()"></button>
            <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
          </div>
          @if (latestKit()) {
            <p>{{ t('growth.concierge.kit.runId') }}: {{ latestKit()!.id }} — {{ latestKit()!.status }}</p>
          }
        </section>

        @if (latestKit()) {
          <section class="card">
            <div class="section-header">
              <h2>{{ t('growth.concierge.marketplaceSeoHook.title') }}</h2>
              <ain-info-hint
                [titleKey]="'growth.concierge.marketplaceSeoHook.hint.title'"
                [descriptionKey]="'growth.concierge.marketplaceSeoHook.hint.description'"
                [bulletKeys]="['growth.concierge.marketplaceSeoHook.hint.bullets.storefront', 'growth.concierge.marketplaceSeoHook.hint.bullets.planning']" />
            </div>
            <div class="form-grid">
              <ain-form-field [label]="t('growth.concierge.marketplaceSeoHook.fields.seoProfile')">
                <ain-growth-seo-profile-selector [(ngModel)]="seoHookForm.seoProfileId" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.concierge.marketplaceSeoHook.fields.targetType')">
                <p-select [options]="seoHookTargetTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="seoHookForm.targetType" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.concierge.marketplaceSeoHook.fields.hookStatus')">
                <p-select [options]="seoHookStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="seoHookForm.hookStatus" appendTo="body" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.concierge.marketplaceSeoHook.fields.offering')">
                <ain-offering-selector [(ngModel)]="seoHookForm.offeringId" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.concierge.marketplaceSeoHook.fields.notes')">
                <textarea pInputTextarea rows="3" [(ngModel)]="seoHookForm.notes"></textarea>
              </ain-form-field>
            </div>
            <div class="toolbar">
              <button pButton type="button" [label]="t('common.save')" (click)="saveMarketplaceSeoHook()" [disabled]="actionBusy() || !canEditSeoHook()"></button>
            </div>
          </section>
        }

        <section class="card">
          <div class="section-header">
            <h2>{{ t('growth.concierge.playbook.title') }}</h2>
            <ain-info-hint
              [titleKey]="'growth.concierge.playbook.hint.title'"
              [descriptionKey]="'growth.concierge.playbook.hint.description'" />
          </div>
          <p-table [value]="playbookSteps()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.concierge.playbook.stepId') }}</th>
                <th>{{ t('growth.concierge.playbook.action') }}</th>
                <th>{{ t('growth.concierge.playbook.contentType') }}</th>
                <th>{{ t('growth.concierge.playbook.channel') }}</th>
                <th>{{ t('growth.concierge.playbook.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.stepId }}</td>
                <td>{{ row.action }}</td>
                <td>{{ row.contentType ?? '—' }}</td>
                <td>{{ row.channel }}</td>
                <td>{{ row.completed ? t('growth.concierge.playbook.completed') : t('growth.concierge.playbook.pending') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-header">
            <h2>{{ t('growth.concierge.policy.title') }}</h2>
            <ain-info-hint
              [titleKey]="'growth.concierge.policy.hint.title'"
              [descriptionKey]="'growth.concierge.policy.hint.description'"
              [bulletKeys]="['growth.concierge.policy.hint.bullets.starter']" />
          </div>
          <div class="form-grid">
            <ain-form-field [label]="t('growth.concierge.policy.enabled')">
              <p-checkbox [(ngModel)]="policyForm.enabled" [binary]="true" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.concierge.policy.paidAllowed')">
              <p-checkbox [(ngModel)]="policyForm.paidChannelsAllowed" [binary]="true" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.concierge.policy.smsFirstAllowed')">
              <p-checkbox [(ngModel)]="policyForm.smsFirstAllowed" [binary]="true" />
            </ain-form-field>
          </div>
          <h3>{{ t('growth.concierge.policy.blockedChannels') }}</h3>
          @for (row of policyForm.blockedChannels; track $index) {
            <div class="row-editor">
              <p-select
                [options]="channelOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="policyForm.blockedChannels[$index].channelKey"
                [placeholder]="t('growth.concierge.policy.channelKey')"
                appendTo="body" />
              <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removeBlockedChannel($index)"></button>
            </div>
          }
          <button pButton type="button" severity="secondary" size="small" [label]="t('growth.concierge.policy.addChannel')" (click)="addBlockedChannel()"></button>

          <h3>{{ t('growth.concierge.policy.channelPriority') }}</h3>
          @for (row of policyForm.channelPriority; track $index) {
            <div class="row-editor">
              <p-select
                [options]="channelOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="policyForm.channelPriority[$index].channelKey"
                [placeholder]="t('growth.concierge.policy.channelKey')"
                appendTo="body" />
              <p-inputNumber [(ngModel)]="policyForm.channelPriority[$index].priority" [placeholder]="t('growth.concierge.policy.priority')" />
              <button pButton type="button" severity="danger" size="small" [label]="t('common.remove')" (click)="removePriorityChannel($index)"></button>
            </div>
          }
          <button pButton type="button" severity="secondary" size="small" [label]="t('growth.concierge.policy.addPriority')" (click)="addPriorityChannel()"></button>
          <div class="toolbar">
            <button pButton type="button" [label]="t('common.save')" (click)="savePolicy()" [disabled]="actionBusy()"></button>
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .dashboard-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; }
    .metric-card h3 { margin: 0 0 .5rem; font-size: .95rem; color: var(--text-color-secondary); }
    .metric-value { font-size: 1.4rem; font-weight: 600; margin: 0; }
    .section-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
    .toolbar { display: flex; flex-wrap: wrap; gap: .5rem; margin-bottom: 1rem; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-bottom: 1rem; }
    .row-editor { display: flex; gap: .5rem; margin-bottom: .5rem; align-items: center; }
    .row-editor input { flex: 1; }
  `]
})
export class GrowthTgeConciergeComponent implements OnInit {
  private readonly marketing = inject(GrowthMarketingService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionBusy = signal(false);
  readonly snapshot = signal<GrowthTgeConciergeSnapshot | null>(null);
  readonly latestKit = signal<GrowthMarketingKitRun | null>(null);
  readonly playbookSteps = signal<PlaybookStepView[]>([]);

  policyForm = {
    enabled: true,
    paidChannelsAllowed: false,
    smsFirstAllowed: false,
    blockedChannels: [] as PolicyChannelRow[],
    channelPriority: [] as PolicyChannelRow[]
  };

  seoHookForm = {
    seoProfileId: null as string | null,
    targetType: 'product',
    hookStatus: 'linked',
    offeringId: null as string | null,
    notes: ''
  };

  readonly seoHookTargetTypeOptions = [
    { value: 'product', label: 'Catalog offering / PDP' },
    { value: 'collection-page', label: 'Collection / PLP' },
    { value: 'content-asset', label: 'Content asset' }
  ];
  readonly seoHookStatusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'linked', label: 'Linked' },
    { value: 'deferred', label: 'Deferred' }
  ];

  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthMarketingChannelOptions(key => this.t(key));
  }

  ngOnInit(): void {
    this.load();
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.marketing.getTgeConcierge().subscribe({
      next: snapshot => {
        this.snapshot.set(snapshot);
        this.bindPolicyForm(snapshot.policy);
        this.playbookSteps.set(this.buildPlaybookSteps(snapshot.playbook));
        if (snapshot.latestKitRunId)
          this.loadLatestKit();
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.concierge.errors.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  quotaLabel(used: number, remaining: number | null): string {
    return remaining == null ? `${used} / ∞` : `${used} / ${used + remaining}`;
  }

  triggerKit(): void {
    this.actionBusy.set(true);
    this.marketing.triggerMarketingKit({ idempotencyKey: `kit-manual-${Date.now()}` }).subscribe({
      next: result => {
        this.latestKit.set(result.run);
        this.bindSeoHookForm(result.run);
        this.load();
        this.actionBusy.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.concierge.errors.triggerFailed'));
        this.actionBusy.set(false);
      }
    });
  }

  loadLatestKit(): void {
    const id = this.snapshot()?.latestKitRunId;
    if (!id) return;
    this.marketing.getMarketingKitRun(id).subscribe({
      next: run => {
        this.latestKit.set(run);
        this.bindSeoHookForm(run);
      },
      error: () => this.error.set(this.t('growth.concierge.errors.kitLoadFailed'))
    });
  }

  canEditSeoHook(): boolean {
    const status = this.latestKit()?.status;
    return status === 'ReadyForReview' || status === 'Approved' || status === 'Scheduled';
  }

  saveMarketplaceSeoHook(): void {
    const id = this.latestKit()?.id;
    if (!id || !this.canEditSeoHook()) return;
    this.actionBusy.set(true);
    this.marketing.updateMarketingKitSeoHook(id, {
      seoProfileId: this.seoHookForm.seoProfileId,
      targetType: this.seoHookForm.targetType,
      hookStatus: this.seoHookForm.hookStatus,
      notes: this.seoHookForm.notes || null,
      offeringId: this.seoHookForm.offeringId
    }).subscribe({
      next: run => {
        this.latestKit.set(run);
        this.bindSeoHookForm(run);
        this.actionBusy.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.concierge.errors.seoHookSaveFailed'));
        this.actionBusy.set(false);
      }
    });
  }

  canApproveBundle(): boolean {
    return this.latestKit()?.status === 'ReadyForReview' || this.snapshot()?.kitStatus === 'ReadyForReview';
  }

  approveBundle(): void {
    const id = this.latestKit()?.id ?? this.snapshot()?.latestKitRunId;
    if (!id) return;
    this.actionBusy.set(true);
    this.marketing.approveMarketingKitBundle(id, {}).subscribe({
      next: run => {
        this.latestKit.set(run);
        this.load();
        this.actionBusy.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.concierge.errors.approveFailed'));
        this.actionBusy.set(false);
      }
    });
  }

  nextPlaybookStep(): PlaybookStepView | undefined {
    return this.playbookSteps().find(step => !step.completed);
  }

  completeNextPlaybookStep(): void {
    const step = this.nextPlaybookStep();
    if (!step) return;
    this.actionBusy.set(true);
    this.marketing.completePlaybookStep(step.stepId, {}).subscribe({
      next: () => {
        this.load();
        this.actionBusy.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.concierge.errors.playbookFailed'));
        this.actionBusy.set(false);
      }
    });
  }

  addBlockedChannel(): void {
    this.policyForm.blockedChannels.push({ channelKey: '', priority: 0 });
  }

  removeBlockedChannel(index: number): void {
    this.policyForm.blockedChannels.splice(index, 1);
  }

  addPriorityChannel(): void {
    this.policyForm.channelPriority.push({ channelKey: '', priority: this.policyForm.channelPriority.length + 1 });
  }

  removePriorityChannel(index: number): void {
    this.policyForm.channelPriority.splice(index, 1);
  }

  savePolicy(): void {
    this.actionBusy.set(true);
    this.marketing.updateOrganicFirstPolicy({
      enabled: this.policyForm.enabled,
      planKey: this.snapshot()?.quota.entitlement.planKey ?? null,
      blockedChannelsJson: JSON.stringify(this.policyForm.blockedChannels.filter(r => r.channelKey.trim()).map(r => r.channelKey.trim())),
      channelPriorityJson: JSON.stringify(
        [...this.policyForm.channelPriority]
          .filter(r => r.channelKey.trim())
          .sort((a, b) => a.priority - b.priority)
          .map(r => r.channelKey.trim())
      ),
      paidChannelsAllowed: this.policyForm.paidChannelsAllowed,
      smsFirstAllowed: this.policyForm.smsFirstAllowed
    }).subscribe({
      next: () => {
        this.load();
        this.actionBusy.set(false);
      },
      error: () => {
        this.error.set(this.t('growth.concierge.errors.policyFailed'));
        this.actionBusy.set(false);
      }
    });
  }

  private bindSeoHookForm(run: GrowthMarketingKitRun): void {
    if (!run.marketplaceSeoHookJson) {
      this.seoHookForm = { seoProfileId: null, targetType: 'product', hookStatus: 'deferred', offeringId: null, notes: '' };
      return;
    }
    try {
      const parsed = JSON.parse(run.marketplaceSeoHookJson) as {
        seoProfileId?: string | null;
        targetType?: string;
        hookStatus?: string;
        offeringId?: string | null;
        notes?: string | null;
      };
      this.seoHookForm = {
        seoProfileId: parsed.seoProfileId ?? null,
        targetType: parsed.targetType ?? 'product',
        hookStatus: parsed.hookStatus ?? 'deferred',
        offeringId: parsed.offeringId ?? null,
        notes: parsed.notes ?? ''
      };
    } catch {
      this.seoHookForm = { seoProfileId: null, targetType: 'product', hookStatus: 'deferred', offeringId: null, notes: run.marketplaceSeoHookJson };
    }
  }

  private bindPolicyForm(policy: GrowthOrganicFirstPolicy): void {
    const blocked = this.parseStringArray(policy.blockedChannelsJson);
    const priority = this.parseStringArray(policy.channelPriorityJson);
    this.policyForm = {
      enabled: policy.enabled,
      paidChannelsAllowed: policy.paidChannelsAllowed,
      smsFirstAllowed: policy.smsFirstAllowed,
      blockedChannels: blocked.map((channelKey, index) => ({ channelKey, priority: index + 1 })),
      channelPriority: priority.map((channelKey, index) => ({ channelKey, priority: index + 1 }))
    };
  }

  private buildPlaybookSteps(progress: GrowthPlaybookProgress): PlaybookStepView[] {
    const completed = this.parseStringArray(progress.completedStepsJson);
    const catalog = [
      'approve-marketing-kit',
      'publish-landing',
      'schedule-social',
      'review-leads',
      'generate-next-brief-from-insight'
    ];
    const next = this.parseStep(progress.nextStepJson);
    return catalog.map(stepId => ({
      stepId,
      action: stepId,
      contentType: next?.stepId === stepId ? next.contentType : null,
      channel: next?.stepId === stepId ? next.channel : 'web',
      day: next?.stepId === stepId ? next.day : 0,
      completed: completed.includes(stepId)
    }));
  }

  private parseStringArray(json: string): string[] {
    try {
      return JSON.parse(json) as string[];
    } catch {
      return [];
    }
  }

  private parseStep(json: string): { stepId: string; contentType: string | null; channel: string; day: number } | null {
    try {
      const parsed = JSON.parse(json) as { stepId?: string; contentType?: string; channel?: string; day?: number };
      if (!parsed.stepId) return null;
      return {
        stepId: parsed.stepId,
        contentType: parsed.contentType ?? null,
        channel: parsed.channel ?? 'web',
        day: parsed.day ?? 0
      };
    } catch {
      return null;
    }
  }
}
